// SAB-644 TEMP diagnostic. Fires a server-side beacon at each terminal outcome of the
// Locance geo gather, so we can read WHY native produces no session without the device
// console. Gated server-side to soap_test_accounts. Strictly non-fatal — must never affect
// the gather. DELETE this file + its call sites + the geo-gather-beacon fn/table once we
// have the answer.

import { supabase } from '@/integrations/supabase/client';
import { Capacitor } from '@capacitor/core';
import { LOCANCE_HTML5_SRC, LOCANCE_PROFILING_SRC } from '@/lib/locance';
import { getGeoShimDiag, getGeoShimLog } from '@/lib/nativeGeolocationShim';

export interface GeoGatherDiag {
  outcome:
    | 'success'
    | 'collector_nonsuccess'
    | 'watchdog_timeout'
    | 'script_load_error'
    | 'scripts_missing'
    | 'lshtml5_threw'
    // SAB-644 native appData path outcomes.
    | 'appdata_success'
    | 'appdata_nonsuccess'
    | 'appdata_threw'
    // SAB-644 TEMP: did lsdp device profiling COMPLETE (tmx_profiling_complete fire) on native,
    // vs merely load. error_message carries profilingDone + lsdp_present.
    | 'native_profiling';
  scripts_loaded?: boolean;
  status_code?: number | null;
  location_code?: string | null;
  consent_state?: string | null;
  session_id?: string | null;
  error_message?: string | null;
  // SAB-644: the appData native path gathers via Capacitor directly (no navigator.geolocation
  // shim), so the shim-derived geo_method/geo_fix/geo_error are null there. Let the caller
  // supply them so we record whether a position was obtained before the POST.
  geo_method?: string | null;
  geo_fix?: boolean | null;
  geo_error?: string | null;
}

// Probe a script URL from THIS context (the WKWebView, on native), so we can tell whether the
// host is reachable at all vs blocked. no-cors: a reachable host resolves opaque (status 0); an
// ATS / network block THROWS. Same-origin URLs (the profiling script) return a real status.
// null / undefined / number are three different things to a validator and read the same in a
// naive snapshot — discriminate them explicitly. `value` is included for non-PII fields only.
function describeVal(v: unknown, omitValue = false): { kind: string; value?: number } {
  let kind: string;
  if (v === null) kind = 'null';
  else if (v === undefined) kind = 'undefined';
  else if (typeof v === 'number') kind = Number.isNaN(v) ? 'nan' : Number.isFinite(v) ? 'number' : 'infinite';
  else kind = typeof v;
  return !omitValue && kind === 'number' ? { kind, value: v as number } : { kind };
}

// Privacy: latitude/longitude report KIND only (never the value); accuracy/altitude/etc carry
// the value — that's the evidence for the accuracy ?? 0 coercion.
function describeCoords(coords: unknown): Record<string, unknown> | null {
  if (!coords || typeof coords !== 'object') return null;
  const c = coords as Record<string, unknown>;
  return {
    latitude: describeVal(c.latitude, true),
    longitude: describeVal(c.longitude, true),
    accuracy: describeVal(c.accuracy),
    altitude: describeVal(c.altitude),
    altitudeAccuracy: describeVal(c.altitudeAccuracy),
    heading: describeVal(c.heading),
    speed: describeVal(c.speed),
  };
}

function snapshot(pos: unknown): { coords: Record<string, unknown> | null; timestamp: { kind: string; value?: number } } | null {
  if (!pos || typeof pos !== 'object') return null;
  const p = pos as Record<string, unknown>;
  return { coords: describeCoords(p.coords), timestamp: describeVal(p.timestamp) };
}

function isPosInstance(pos: unknown): boolean | null {
  if (pos == null || typeof pos !== 'object') return null;
  return typeof GeolocationPosition !== 'undefined' && pos instanceof GeolocationPosition;
}
function isCoordsInstance(pos: unknown): boolean | null {
  if (pos == null || typeof pos !== 'object') return null;
  const c = (pos as { coords?: unknown }).coords;
  if (c == null || typeof c !== 'object') return null;
  return typeof GeolocationCoordinates !== 'undefined' && c instanceof GeolocationCoordinates;
}

// Web baseline for the diff: sample a REAL browser position — but only when permission is
// already granted, so we never trigger a surprise prompt. Returns null otherwise.
async function sampleWebPosition(): Promise<GeolocationPosition | null> {
  try {
    if (typeof navigator === 'undefined' || !navigator.geolocation) return null;
    // Cast to any locally: `PermissionName` is a type-only DOM name (no runtime global), so the
    // crash-class no-undef lint gate flags it. Avoid the identifier entirely.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const perm = await (navigator.permissions as any)
      ?.query({ name: 'geolocation' })
      .catch(() => null);
    if (perm && perm.state !== 'granted') return null;
    return await new Promise<GeolocationPosition | null>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (p) => resolve(p),
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 60_000 },
      );
    });
  } catch {
    return null;
  }
}

async function probe(url: string): Promise<{ reachable: boolean; status: number | null; error: string | null }> {
  try {
    const res = await fetch(url, { method: 'GET', mode: 'no-cors', cache: 'no-store' });
    return { reachable: true, status: res.status || 0, error: null };
  } catch (e) {
    return { reachable: false, status: null, error: ((e as Error)?.message ?? 'fetch failed').slice(0, 200) };
  }
}

export function sendGeoGatherBeacon(diag: GeoGatherDiag): void {
  // Fire-and-forget; probes + invoke run off the gather's critical path.
  void (async () => {
    try {
      const html5Url = LOCANCE_HTML5_SRC;
      const profilingUrl = (typeof window !== 'undefined' ? window.location.origin : '') + LOCANCE_PROFILING_SRC;
      const [h5, pf] = await Promise.all([probe(html5Url), probe(profilingUrl)]);
      const shim = getGeoShimDiag();
      const w = typeof window !== 'undefined' ? (window as unknown as { lshtml5?: unknown; lsdp?: unknown }) : undefined;

      // Position-shape capture. Native: from the shim (RAW Capacitor pre-conversion + converted).
      // Web: sample one real browser position as the diff baseline (raw == converted there).
      let rawCoords: unknown = null;
      let convertedCoords: unknown = null;
      let posInstance: boolean | null = null;
      let coordsInstance: boolean | null = null;
      let callbackCount: number | null = null;
      if (shim) {
        rawCoords = snapshot(shim.rawPos);
        convertedCoords = snapshot(shim.convertedPos);
        posInstance = isPosInstance(shim.convertedPos);
        coordsInstance = isCoordsInstance(shim.convertedPos);
        callbackCount = shim.callbackCount;
      } else if (!Capacitor.isNativePlatform()) {
        const sampled = await sampleWebPosition();
        if (sampled) {
          rawCoords = snapshot(sampled);
          convertedCoords = rawCoords;
          posInstance = isPosInstance(sampled);
          coordsInstance = isCoordsInstance(sampled);
        }
      }
      await supabase.functions.invoke('geo-gather-beacon', {
        body: {
          platform: Capacitor.getPlatform(),
          is_native: Capacitor.isNativePlatform(),
          user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 300) : null,
          outcome: diag.outcome,
          scripts_loaded: diag.scripts_loaded ?? null,
          lshtml5_present: w ? !!w.lshtml5 : null,
          lsdp_present: w ? !!w.lsdp : null,
          html5_url: html5Url,
          html5_reachable: h5.reachable,
          html5_status: h5.status,
          html5_error: h5.error,
          profiling_url: profilingUrl,
          profiling_reachable: pf.reachable,
          profiling_status: pf.status,
          profiling_error: pf.error,
          status_code: diag.status_code ?? null,
          location_code: diag.location_code ?? null,
          consent_state: diag.consent_state ?? null,
          // SAB-644: caller-supplied values win (the appData path has no shim); fall back to
          // the shim for the HTML5/web path.
          geo_method: diag.geo_method ?? shim?.method ?? null,
          geo_fix: diag.geo_fix ?? (shim ? shim.delivered : null),
          geo_error: diag.geo_error ?? shim?.error ?? null,
          raw_coords: rawCoords,
          converted_coords: convertedCoords,
          is_pos_instance: posInstance,
          is_coords_instance: coordsInstance,
          callback_count: callbackCount,
          geo_call_log: getGeoShimLog(),
          session_id: diag.session_id ?? null,
          error_message: diag.error_message ?? null,
        },
      });
    } catch {
      /* diagnostic must never affect the gather */
    }
  })();
}
